import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  forwardRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Jodit } from 'jodit/es2021/jodit.fat.min.js';

const DEFAULT_BUTTONS = [
  'undo',
  'redo',
  '|',
  'bold',
  'italic',
  'underline',
  'strikethrough',
  '|',
  'superscript',
  'subscript',
  '|',
  'ul',
  'ol',
  'indent',
  'outdent',
  '|',
  'font',
  'fontsize',
  'brush',
  'paragraph',
  '|',
  'align',
  '|',
  'link',
  'image',
  'table',
  '|',
  'hr',
  'eraser',
  '|',
  'fullsize'
];

@Component({
  selector: 'app-rich-text-editor',
  standalone: false,
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true
    }
  ]
})
export class RichTextEditorComponent
  implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() placeholder: string = '';
  @Input() height: number = 300;
  @Input() buttons: string[] = DEFAULT_BUTTONS;
  @Input() disabled: boolean = false;

  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef<
    HTMLTextAreaElement
  >;

  /**
   * Emits the new HTML only for edits made by the user in the editor. It never emits for a value
   * pushed in from the model (initial load or a later writeValue), so a parent can use it to mark
   * a form dirty or trigger autosave without being dirtied by simply loading content. Jodit fires
   * its own change event when a value is set programmatically, which the ControlValueAccessor
   * onChange also receives, so (ngModelChange) is not safe for that purpose.
   */
  @Output() userChange = new EventEmitter<string>();
  private editor!: Jodit;
  private isWritingValue: boolean = false;
  private value: string = '';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.editor = Jodit.make(this.editorContainer.nativeElement, {
      height: this.height,
      placeholder: this.placeholder,
      buttons: this.buttons,
      buttonsMD: this.buttons,
      buttonsSM: this.buttons,
      buttonsXS: this.buttons,
      readonly: this.disabled,
      toolbarAdaptive: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showXPathInStatusbar: false,
      // Attach popups (table picker, link and image forms) to the document body.
      //
      // Without this, Jodit attaches them to the nearest positioned ancestor of the editor. Inside
      // a MatDialog that ancestor is '.cdk-global-overlay-wrapper', which the CDK gives
      // 'pointer-events: none' (only the pane inside it re-enables pointer events). The popup then
      // renders on top but inherits pointer-events: none, so it cannot be hovered or clicked: the
      // table grid never highlights, and clicking a link or image form is treated as a click
      // outside the popup, which closes it. Anchoring to the body, which is what already happens
      // for an editor on a normal page, keeps popups interactive everywhere. Jodit repositions them
      // on window and ancestor scroll, so this does not detach them from the toolbar button.
      popupRoot: document.body
    });

    this.editor.value = this.value;

    this.editor.events.on('change', (newValue: string) => {
      this.value = newValue;
      this.onChange(newValue);
      this.onTouched();
      if (!this.isWritingValue) {
        this.userChange.emit(newValue);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.editor) {
      this.editor.destruct();
    }
  }

  writeValue(html: string | null): void {
    this.value = html || '';
    if (this.editor) {
      // Jodit fires its change event synchronously on assignment, so flag the write as programmatic.
      this.isWritingValue = true;
      try {
        this.editor.value = this.value;
      } finally {
        this.isWritingValue = false;
      }
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.editor) {
      this.editor.setReadOnly(isDisabled);
    }
  }
}
